import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, OnInit, Output, effect, inject, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ChatService } from '../../../services/chat.service';
import { ChatRealtimeService } from '../../../services/chat-realtime.service';
import { UserService } from '../../../services/user.service';
import { ChatRoomDTO } from '../../../models/chat-room-dto';
import { ChatCreateRoomModalComponent } from '../chat-create-room-modal/chat-create-room-modal.component';

@Component({
  selector: 'app-chat-room-list',
  standalone: true,
  imports: [],
  templateUrl: './chat-room-list.component.html',
  styleUrl: './chat-room-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatRoomListComponent implements OnInit {
  @Input() selectedRoomId: string | null = null;
  @Output() roomSelected = new EventEmitter<ChatRoomDTO>();

  private chatService = inject(ChatService);
  chatRealtimeService = inject(ChatRealtimeService);
  private userService = inject(UserService);
  private modalService = inject(NgbModal);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  // Estado único de salas de ChatService (CHAT_REVISION.md B8): la lista ya no pide GetRooms por su
  // cuenta; se repinta sola con cualquier recarga (signals en plantilla OnPush).
  readonly rooms = this.chatService.rooms;
  readonly roomsLoadError = this.chatService.roomsLoadError;

  canCreateRoom = true;

  constructor() {
    // CanCreateRoom solo depende del usuario y de la instalación (el cruce operario se hace por
    // instalación): se pide al abrir la lista y al cambiar de instalación, NO en cada recarga de salas
    // como antes (una petición extra por cada mensaje recibido).
    effect(() => {
      this.userService.selectedInstallationId();
      untracked(() => this.loadCanCreateRoom());
    });
  }

  ngOnInit(): void {
    // Al abrir el chat se muestran ya las salas que había en memoria (el estado se mantiene al día
    // desde que arranca la sesión), y se pide una recarga por si se ha perdido algún evento.
    this.chatService.notifyRoomsChanged();
  }

  private loadCanCreateRoom(): void {
    this.chatService.canCreateRoom().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: canCreate => { this.canCreateRoom = canCreate; this.cdr.detectChanges(); },
      // Fallo silencioso: si la comprobación falla, se deja visible el botón — CreateRoom valida
      // lo mismo en el backend, así que no queda expuesto ningún caso real sin proteger.
      error: () => {}
    });
  }

  selectRoom(room: ChatRoomDTO): void {
    this.roomSelected.emit(room);
  }

  /** CHAT_REVISION.md C16: si es hoy, solo la hora; si no, solo el día (mismo criterio que los separadores de día de la conversación). */
  roomDateLabel(dateStr?: string): string {
    if (!dateStr) return '';

    const date = new Date(dateStr);
    const now = new Date();

    if (date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate()) {
      return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    }

    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
  }

  openCreateRoom(): void {
    const modalRef = this.modalService.open(ChatCreateRoomModalComponent, { centered: true });

    modalRef.closed.subscribe((room: ChatRoomDTO | undefined) => {
      if (!room) return;
      this.chatService.notifyRoomsChanged();
      this.roomSelected.emit(room);
    });
  }
}
